import { motion } from "framer-motion";
import { useState } from "react";

const SERVICES = [
    {
        n: "01",
        name: "Interior Painting",
        blurb:
            "From baseboard to crown. Zero-fume finishes, tape-line perfect, walls that photograph themselves.",
        accent: "#FF2BD6",
    },
    {
        n: "02",
        name: "Exterior Painting",
        blurb:
            "Stucco, siding, trim, doors — colors engineered for the SoFla sun so the curb appeal never fades.",
        accent: "#FF4500",
    },
    {
        n: "03",
        name: "Commercial Painting",
        blurb:
            "Storefronts, offices, HOAs. On-brand color systems and after-hours crews so business never blinks.",
        accent: "#FFE600",
    },
    {
        n: "04",
        name: "Cabinet Refinishing",
        blurb:
            "Bring dated cabinets back from the dead. Sanded, primed, sprayed, sealed — like a new kitchen for a fifth of the price.",
        accent: "#00FF85",
    },
    {
        n: "05",
        name: "Pressure Washing",
        blurb:
            "Driveways, decks, roofs, pool cages — high-pressure prep so every coat sticks like it's got royalty behind it.",
        accent: "#00E5FF",
    },
    {
        n: "06",
        name: "Drywall Installation",
        blurb:
            "New rooms, additions, or after the reno — hung, mudded, taped, and sanded flat enough to eat off.",
        accent: "#A855F7",
    },
    {
        n: "07",
        name: "Drywall Repair",
        blurb:
            "Popcorn, patches, storm damage, doorknob dents — invisible fixes, ready-to-paint in a single visit.",
        accent: "#FF2BD6",
    },
    {
        n: "08",
        name: "Wallpaper Removal",
        blurb:
            "Even the wallpaper your grandma glued on in '82. Off the wall, no gouges, no ghosts.",
        accent: "#FF4500",
    },
];

/**
 * Numbered manifesto chapters — Bento Tetris skeleton grid.
 */
export default function Manifesto() {
    return (
        <section
            id="services"
            data-testid="services"
            className="relative bg-black py-24 md:py-40"
        >
            <div className="px-6 md:px-16">
                {/* Section header */}
                <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
                    <div className="col-span-12 md:col-span-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 pt-2">
                        [ Ch. 04 ]
                        <br />
                        Manifesto
                    </div>
                    <h2 className="col-span-12 md:col-span-10 font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] tracking-tight">
                        Eight ways
                        <br />
                        we <span className="rainbow-text">crown</span> a
                        surface.
                    </h2>
                </div>

                {/* Bento */}
                <div className="border border-white/10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
                    {SERVICES.map((s, i) => (
                        <ServiceTile key={s.n} s={s} index={i} />
                    ))}
                </div>

                <p className="mt-10 font-mono text-xs tracking-[0.2em] uppercase text-white/50 max-w-2xl">
                    Not sure which one you need? Call Paul. He walks the job
                    himself.
                </p>
            </div>
        </section>
    );
}

function ServiceTile({ s, index }) {
    const [hover, setHover] = useState(false);
    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
                duration: 0.8,
                delay: index * 0.06,
                ease: [0.19, 1, 0.22, 1],
            }}
            onHoverStart={() => setHover(true)}
            onHoverEnd={() => setHover(false)}
            data-testid={`service-${s.n}`}
            className="relative glass p-8 md:p-10 min-h-[280px] flex flex-col justify-between group overflow-hidden cursor-crosshair"
        >
            {/* Rainbow splash on hover */}
            <motion.div
                aria-hidden
                initial={false}
                animate={{ opacity: hover ? 1 : 0, scale: hover ? 1 : 0.6 }}
                transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
                className="absolute -inset-1 pointer-events-none"
                style={{
                    background: `radial-gradient(circle at 50% 40%, ${s.accent}55, transparent 65%)`,
                }}
            />
            {/* Corner ticks */}
            <div className="absolute top-3 right-3 w-4 h-4 border-t border-r border-white/40" />
            <div className="absolute bottom-3 left-3 w-4 h-4 border-b border-l border-white/40" />

            <div className="relative flex items-start justify-between">
                <span className="font-mono text-xs tracking-[0.28em] uppercase text-white/60">
                    [ {s.n} ]
                </span>
                <motion.span
                    animate={{ rotate: hover ? 45 : 0 }}
                    transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
                    className="font-mono text-xl"
                    style={{ color: hover ? s.accent : "#ffffff70" }}
                >
                    +
                </motion.span>
            </div>

            <div className="relative">
                <h3 className="font-display uppercase text-3xl md:text-4xl leading-[0.9] tracking-tight mb-4">
                    {s.name}
                </h3>
                <p className="font-mono text-[13px] leading-relaxed text-white/60 max-w-xs">
                    {s.blurb}
                </p>
            </div>
        </motion.div>
    );
}
